import { Fragment, createContext, useContext, useEffect, useState } from 'react';
import {
  GrcButton,
  GrcButtonStylingMode,
  GrcButtonType,
  GrcDrawer,
  GrcDrawerOpenedStateMode,
  GrcDrawerPanelLocation,
  GrcDrawerRevealMode,
  GrcPopup,
  GrcTextBox
} from '@grc/ui-package';
import TenantDetailsFields from './tenant-details-fields';
import {
  TenantCommonDetails,
  commonDetailsFromTenant,
  isFilled,
  isOptionalHttpsUrl,
  useFieldState
} from './tenant-form';
import { TenantDetails, UpdateTenantRequest } from './tenant-types';

const PANEL_WIDTH = 480;

type EditState = TenantCommonDetails & { email: string };

interface TenantEditDrawerProps {
  tenant: TenantDetails | null;
  onCancel: () => void;
  onUpdate: (id: string, request: UpdateTenantRequest) => Promise<void>;
  onActivate: (id: string) => Promise<void>;
}

interface EditPanelValue {
  tenant: TenantDetails | null;
  /** Bumped on every open, so the form remounts with the saved values each time. */
  session: number;
  onCancel: () => void;
  onUpdate: (id: string, request: UpdateTenantRequest) => Promise<void>;
  onRequestActivate: () => void;
}

const EditPanelContext = createContext<EditPanelValue | null>(null);

interface TenantEditFormProps {
  tenant: TenantDetails;
  onCancel: () => void;
  onUpdate: (id: string, request: UpdateTenantRequest) => Promise<void>;
  onRequestActivate: () => void;
}

function TenantEditForm({ tenant, onCancel, onUpdate, onRequestActivate }: TenantEditFormProps) {
  const { values, touched, setField, markTouched } = useFieldState<EditState>({
    ...commonDetailsFromTenant(tenant),
    email: tenant.email ?? ''
  });
  const [submitting, setSubmitting] = useState(false);

  const nameValid = isFilled(values.name);
  const carrierPortalUrlValid = isOptionalHttpsUrl(values.carrierPortalUrl);
  const customerPortalUrlValid = isOptionalHttpsUrl(values.customerPortalUrl);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await onUpdate(tenant.id, values);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <header className="tenant-edit-header">
        <h2>Edit Tenant</h2>
        <GrcButton
          id="tenant-edit-close"
          icon="close"
          type={GrcButtonType.Normal}
          stylingMode={GrcButtonStylingMode.Text}
          onClick={onCancel}
        />
      </header>

      <div className="tenant-edit-body">
        <div className="tenant-form tenant-form-single">
          <TenantDetailsFields
            values={values}
            setField={setField}
            markTouched={markTouched}
            nameInvalid={!!touched.name && !nameValid}
            carrierPortalUrlInvalid={!!touched.carrierPortalUrl && !carrierPortalUrlValid}
            customerPortalUrlInvalid={!!touched.customerPortalUrl && !customerPortalUrlValid}
          >
            <GrcTextBox
              id="tenant-email"
              label="Email"
              labelMode="static"
              stylingMode="outlined"
              mode="email"
              value={values.email}
              onValueChanged={setField('email')}
            />
          </TenantDetailsFields>
        </div>

        <div className="activation-section">
          <div className="activation-status">
            Status: <strong>{tenant.active ? 'Active' : 'Inactive'}</strong>
          </div>
          {!tenant.active && (
            <GrcButton
              id="tenant-activate-button"
              text="Activate Tenant"
              type={GrcButtonType.Success}
              stylingMode={GrcButtonStylingMode.Outlined}
              onClick={onRequestActivate}
            />
          )}
        </div>
      </div>

      <div className="form-actions">
        <GrcButton
          id="tenant-cancel-button"
          text="Cancel"
          type={GrcButtonType.Normal}
          stylingMode={GrcButtonStylingMode.Outlined}
          onClick={onCancel}
        />
        <GrcButton
          id="tenant-save-button"
          text="Save"
          type={GrcButtonType.Default}
          stylingMode={GrcButtonStylingMode.Contained}
          disabled={!nameValid || !carrierPortalUrlValid || !customerPortalUrlValid || submitting}
          onClick={handleSubmit}
        />
      </div>
    </>
  );
}

/**
 * The drawer panel. GrcDrawer discards its children — it renders a hardcoded empty
 * div — so the panel arrives through `component`, which DevExtreme renders as a
 * portal back into this React tree. That is also why the form's props come in by
 * context: `component` takes a type, not an element, so nothing can be passed to it.
 *
 * The section is always rendered, and always at PANEL_WIDTH. In overlap + slide mode
 * DevExtreme sizes the panel from the template's *measured* width, once, when the
 * widget mounts (ui.drawer.js `_render` -> `refreshPanelElementSize(true)`), and never
 * re-measures. Return null here and it measures an empty panel at 0 and pins the
 * overlay to `width: 0` for good — the panel slides in as a bare sliver with the
 * fields clipped out of sight.
 */
function TenantEditPanel() {
  const value = useContext(EditPanelContext);

  return (
    <section className="tenant-edit-panel" style={{ width: PANEL_WIDTH }} aria-label="Edit tenant">
      {value?.tenant && (
        <TenantEditForm
          key={value.session}
          tenant={value.tenant}
          onCancel={value.onCancel}
          onUpdate={value.onUpdate}
          onRequestActivate={value.onRequestActivate}
        />
      )}
    </section>
  );
}

/**
 * Editing happens over the still-visible list rather than in a modal: nothing about
 * it needs the room, and the row being edited stays in view behind it.
 */
export default function TenantEditDrawer({ tenant, onCancel, onUpdate, onActivate }: TenantEditDrawerProps) {
  // Keeps the panel populated while it slides out; clearing on `tenant` alone would
  // blank it for the length of the close animation.
  const [shown, setShown] = useState<TenantDetails | null>(tenant);
  const [session, setSession] = useState(0);
  const [showActivateConfirm, setShowActivateConfirm] = useState(false);
  const [activating, setActivating] = useState(false);

  useEffect(() => {
    if (tenant) {
      setShown(tenant);
      setSession(previous => previous + 1);
    } else {
      setShowActivateConfirm(false);
    }
  }, [tenant]);

  const handleActivateConfirm = async () => {
    if (!tenant) return;
    setActivating(true);
    try {
      await onActivate(tenant.id);
      setShowActivateConfirm(false);
    } finally {
      setActivating(false);
    }
  };

  const opened = tenant !== null;

  return (
    <Fragment>
      <div className={opened ? 'tenant-edit-drawer tenant-edit-drawer-open' : 'tenant-edit-drawer'}>
        <EditPanelContext.Provider
          value={{
            tenant: shown,
            session,
            onCancel,
            onUpdate,
            onRequestActivate: () => setShowActivateConfirm(true)
          }}
        >
          <GrcDrawer
            id="tenant-edit"
            opened={opened}
            openedStateMode={GrcDrawerOpenedStateMode.Overlap}
            position={GrcDrawerPanelLocation.Right}
            revealMode={GrcDrawerRevealMode.Slide}
            minSize={0}
            maxSize={PANEL_WIDTH}
            shading
            component={TenantEditPanel}
          />
        </EditPanelContext.Provider>
      </div>

      {/*
        Outside the drawer container: the confirm is a decision about the tenant, not
        part of the panel, and rendering a popup from inside a DevExtreme template
        buys nothing.
      */}
      <GrcPopup
        visible={showActivateConfirm}
        onHiding={() => !activating && setShowActivateConfirm(false)}
        dragEnabled={false}
        hideOnOutsideClick={false}
        showCloseButton={!activating}
        showTitle={true}
        title="Activate Tenant?"
        width={480}
        height="auto"
      >
        <div className="activate-confirm-body">
          <p className="warning-text">
            Activating a tenant can only be done <strong>once</strong>.
          </p>
          <p>
            Please ensure all tenant details are configured correctly before proceeding. This action
            cannot be undone.
          </p>
          <div className="form-actions">
            <GrcButton
              id="activate-cancel-button"
              text="Cancel"
              type={GrcButtonType.Normal}
              stylingMode={GrcButtonStylingMode.Outlined}
              disabled={activating}
              onClick={() => setShowActivateConfirm(false)}
            />
            <GrcButton
              id="activate-confirm-button"
              text="Activate"
              type={GrcButtonType.Success}
              stylingMode={GrcButtonStylingMode.Contained}
              disabled={activating}
              onClick={handleActivateConfirm}
            />
          </div>
        </div>
      </GrcPopup>
    </Fragment>
  );
}
